import React, { useState } from 'react';

// ══════════════════════════════════════════════════════════════════════
//  CLASS GUIDE  —  quiet, always-available orientation for members
//
//  A collapsed one-line disclosure that sits between the Workouts tab bar
//  and the tab content on BOTH Schedule and On Demand. Opens into a calm
//  glass panel explaining the two membership tabs and the two difficulty
//  tags. Design: docs/ux/class-guide-preview.html (Treatment B, chosen by
//  the product owner 2026-09-21 and rendered there with the LIVE theme).
//
//  WHY NO DISMISS AND NO AUTO-OPEN:
//    A dismissible card disappears exactly when it becomes useful — three
//    weeks in, the first time a member meets a Strong+ badge. A panel that
//    opens itself is the clunky move. So: always there, always closed until
//    asked, no "seen" state to persist and nothing to go stale.
//
//  TWO PRODUCTION THEME TRAPS — do NOT "simplify" these to the obvious vars:
//    1. `--mom-warm-gray` is set IDENTICAL to `--mom-charcoal` (#3e2f2f) in
//       production, so secondary text styled the conventional way renders at
//       full heading strength and the hierarchy collapses.
//    2. `--mom-cream-dark` is set IDENTICAL to `--mom-cream` (#faf5f1), so a
//       hairline drawn with it is INVISIBLE.
//    Every muted tone here is therefore derived with color-mix from charcoal
//    toward cream. See .claude/agent-memory/lead/reference_live_theme_values.
//
//  Colour appears only on the difficulty chips (Classic, Ease In, Strong+),
//  rendered at their real size and colour so the guide teaches recognition of
//  the badges members actually see on tiles. The tab rows are deliberately plain,
//  because tabs carry no colour in the UI either.
// ══════════════════════════════════════════════════════════════════════

const MUTED = 'color-mix(in srgb, var(--mom-charcoal) 60%, var(--mom-cream))';
const LABEL_TONE = 'color-mix(in srgb, var(--mom-charcoal) 78%, var(--mom-cream))';

function Chevron({ open }) {
    return (
        <svg width="10" height="7" viewBox="0 0 10 7" fill="none" aria-hidden="true"
            style={{ transition: 'transform 0.22s ease', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}>
            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

// One row of the guide. `chipColor` non-null ⇒ render the real badge chip
// (Ease In / Strong+); null ⇒ plain micro-caps label (Schedule / On Demand).
function GuideItem({ label, chipColor, head, body }) {
    if (!head && !body) return null;
    return (
        <div>
            <div style={{ marginBottom: 7 }}>
                {chipColor ? (
                    <span style={{
                        display: 'inline-block', padding: '4px 10px', borderRadius: 999,
                        fontFamily: 'var(--mom-font-sans)', fontSize: 10.5, fontWeight: 700,
                        letterSpacing: '0.06em', textTransform: 'uppercase',
                        background: chipColor, color: '#fff',
                    }}>{label}</span>
                ) : (
                    <span style={{
                        display: 'inline-block', fontFamily: 'var(--mom-font-sans)', fontSize: 10.5,
                        fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: LABEL_TONE,
                    }}>{label}</span>
                )}
            </div>
            {head && (
                <div style={{
                    fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 15.5,
                    lineHeight: 1.45, color: 'var(--mom-charcoal)', marginBottom: 5,
                }}>{head}</div>
            )}
            {body && (
                <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13.5, lineHeight: 1.65, color: MUTED }}>{body}</div>
            )}
        </div>
    );
}

/**
 * @param {object}  siteConfig       admin copy source (all strings fall back inline)
 * @param {boolean} isMobile         single-column below 600px
 * @param {string}  strongPlusColor  passed in so the Strong+ teal has ONE definition
 *                                   (it lives in Workouts.js as STRONG_PLUS_COLOR — TAG-001)
 * @param {number}  maxWidth         matches the active tab's content container so the
 *                                   guide lines up with the cards beneath it
 */
export default function ClassGuide({ siteConfig = {}, isMobile = false, strongPlusColor = '#4f7c7a', maxWidth = 800 }) {
    const [open, setOpen] = useState(false);
    const [hover, setHover] = useState(false);

    const c = siteConfig || {};
    // Tab names come from the SAME keys the tab bar itself reads, so renaming a
    // tab renames it here too — the guide can never describe a tab that no
    // longer goes by that name.
    const scheduleLabel = c.workoutsTabUpcoming || 'Schedule';
    const onDemandLabel = c.workoutsTabOnDemand || 'On Demand';

    const items = [
        {
            label: scheduleLabel, chipColor: null,
            head: c.classGuideScheduleHead || 'This is your weekly plan.',
            body: c.classGuideScheduleBody || 'Three 15-minute workouts each week, so you always know what to do next. Just follow along!',
        },
        {
            label: onDemandLabel, chipColor: null,
            head: c.classGuideOnDemandHead || 'More classes, whenever you want them.',
            body: c.classGuideOnDemandBody || 'Explore the full workout library anytime. Perfect for off days, extra movement, or simply choosing what feels good that day.',
        },
        {
            // Labels hardcoded by owner decision — fixed taxonomy (TAG-001).
            // Classic added 2026-10-13 (brand pink), listed first: the tag row
            // reads in Schedule order — Classic left, Strong+ right.
            label: 'Classic', chipColor: 'var(--mom-primary)', tag: true,
            head: c.classGuideClassicHead || 'The signature class, just as you know it.',
            body: c.classGuideClassicBody || 'Danielle’s classic 15-minute workout at a steady pace. On days with two classes, it’s the one on the left.',
        },
        {
            label: 'Ease In', chipColor: 'var(--mom-success)', tag: true,
            head: c.classGuideEaseInHead || 'Gentler classes for when you’re easing back in.',
            body: c.classGuideEaseInBody || 'A slower pace with more time to learn each movement. A great place to start or come back to anytime.',
        },
        {
            label: 'Strong+', chipColor: strongPlusColor, tag: true,
            head: c.classGuideStrongPlusHead || 'Heavier weights for when you’re ready.',
            body: c.classGuideStrongPlusBody || 'More focused strength work using heavier weights, with options to scale back whenever you need.',
        },
    ];

    return (
        <div style={{ maxWidth, margin: '0 auto 4px' }}>
            {/* The trigger is a REAL button, in brand pink, centred.
                v1 shipped this as a muted micro-caps text line — too easy to miss, which
                defeated the point (product owner feedback 2026-09-21). Solid `--mom-primary`
                rather than the `primary → primary-dark` GRADIENT used by Subscribe/checkout
                CTAs: unmistakably clickable, while staying one rung below the conversion
                buttons so it never competes with them for the eye. */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 8px 12px' }}>
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                    aria-expanded={open}
                    style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9,
                        background: hover ? 'var(--mom-primary-dark)' : 'var(--mom-primary)',
                        color: 'var(--mom-white)', border: 0, borderRadius: 999,
                        padding: '11px 22px', cursor: 'pointer',
                        fontFamily: 'var(--mom-font-sans)', fontSize: 13.5, fontWeight: 700,
                        letterSpacing: '0.02em', lineHeight: 1,
                        boxShadow: hover
                            ? '0 4px 14px color-mix(in srgb, var(--mom-primary) 38%, transparent)'
                            : '0 2px 10px color-mix(in srgb, var(--mom-primary) 28%, transparent)',
                        transform: hover ? 'translateY(-1px)' : 'none',
                        transition: 'background 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
                    }}
                >
                    {c.classGuideTitle || 'Your Class Guide'}
                    <Chevron open={open} />
                </button>
            </div>

            {open && (
                <div style={{
                    background: 'rgba(255,255,255,0.5)',
                    backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
                    border: '1px solid rgba(255,255,255,0.65)', borderRadius: 16,
                    padding: isMobile ? '20px 18px 22px' : '22px 24px 24px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                    animation: 'fadeIn 0.28s ease',
                }}>
                    <div style={{
                        fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 20,
                        textAlign: 'center', color: 'var(--mom-charcoal)', marginBottom: 22,
                    }}>{c.classGuideTitle || 'Your Class Guide'}</div>
                    {/* Two rows on desktop: the two tabs, then the three difficulty
                        tags side by side (an odd fifth item in a 2-column grid would
                        leave a hole). One column on a phone, as before. */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                        gap: isMobile ? 24 : '26px 40px',
                    }}>
                        {items.filter((it) => !it.tag).map((it) => <GuideItem key={it.label} {...it} />)}
                    </div>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                        gap: isMobile ? 24 : '26px 28px',
                        marginTop: isMobile ? 24 : 26,
                    }}>
                        {items.filter((it) => it.tag).map((it) => <GuideItem key={it.label} {...it} />)}
                    </div>
                </div>
            )}
        </div>
    );
}
